import React, { useState, useEffect } from 'react';
import {
  FiFileText,
  FiUser,
  FiCalendar,
  FiMail,
  FiDownload,
  FiEye,
  FiX,
  FiTrash2,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { adminApi } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { showToast } from '../../utils/toast';

interface TermsTemplate {
  _id: string;
  name: string;
  email: string;
  termsPrintedName: string;
  termsEmail: string;
  termsAcceptedAt: string;
  termsSignature: string;
  role: string;
  createdAt: string;
}

const PAGE_SIZE = 20;

const TermsTemplates: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<TermsTemplate | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<TermsTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['admin', 'terms-templates', page, PAGE_SIZE, debouncedSearch],
    queryFn: () =>
      adminApi.getTermsTemplates({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
      }),
    placeholderData: (prev) => prev,
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => adminApi.deleteUser(userId),
    onSuccess: () => {
      showToast('success', 'Terms template deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin', 'terms-templates'] });
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to delete terms template');
    },
  });

  const handleView = (template: TermsTemplate) => {
    setSelectedTemplate(template);
    setShowModal(true);
  };

  const handleDeleteClick = (template: TermsTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete?._id) {
      deleteUserMutation.mutate(templateToDelete._id);
    }
  };

  const handleDownload = (template: TermsTemplate) => {
    const acceptanceDate = new Date(template.termsAcceptedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Create a comprehensive downloadable HTML document with full terms
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Terms & Conditions Agreement - ${template.termsPrintedName}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Times New Roman', serif; 
            max-width: 900px; 
            margin: 0 auto; 
            padding: 40px 20px; 
            line-height: 1.6;
            color: #333;
            background: #fff;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 3px solid #10B981;
            padding-bottom: 20px;
          }
          .header h1 {
            font-size: 28px;
            color: #10B981;
            margin-bottom: 10px;
          }
          .header h2 {
            font-size: 20px;
            color: #666;
            font-weight: normal;
          }
          .section {
            margin-bottom: 40px;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 22px;
            font-weight: bold;
            color: #10B981;
            margin-bottom: 20px;
            border-left: 4px solid #10B981;
            padding-left: 15px;
          }
          .disclosure-box {
            background: #fff3cd;
            border: 2px solid #ffc107;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .disclosure-box h3 {
            color: #856404;
            font-size: 18px;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .disclosure-box p {
            color: #856404;
            font-weight: 600;
            margin-bottom: 10px;
          }
          .point {
            margin-bottom: 25px;
            padding-left: 20px;
          }
          .point h4 {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 10px;
            color: #333;
          }
          .point p {
            margin-bottom: 8px;
            text-align: justify;
          }
          .point ul {
            margin-left: 30px;
            margin-top: 10px;
          }
          .point li {
            margin-bottom: 5px;
          }
          .divider {
            text-align: center;
            margin: 40px 0;
            font-size: 24px;
            color: #999;
          }
          .signature-section {
            margin-top: 50px;
            border-top: 2px solid #ddd;
            padding-top: 30px;
          }
          .signature-section h3 {
            font-size: 20px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 30px;
            color: #333;
          }
          .checkbox-list {
            margin-bottom: 30px;
          }
          .checkbox-item {
            margin-bottom: 12px;
            padding-left: 25px;
            position: relative;
          }
          .checkbox-item::before {
            content: "✔";
            position: absolute;
            left: 0;
            color: #10B981;
            font-weight: bold;
            font-size: 16px;
          }
          .signature-fields {
            margin-top: 40px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
            margin-bottom: 30px;
          }
          .signature-field {
            margin-bottom: 20px;
          }
          .signature-field label {
            display: block;
            font-weight: bold;
            margin-bottom: 8px;
            color: #555;
          }
          .signature-field .value {
            border-bottom: 2px solid #333;
            padding-bottom: 5px;
            min-height: 30px;
            font-size: 16px;
          }
          .signature-img-container {
            margin-top: 20px;
            text-align: center;
          }
          .signature-img {
            max-width: 500px;
            border: 2px solid #ddd;
            padding: 15px;
            background: #f9f9f9;
            display: inline-block;
          }
          .user-info {
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 30px;
          }
          .user-info h4 {
            font-size: 18px;
            margin-bottom: 15px;
            color: #10B981;
          }
          .user-info-row {
            display: grid;
            grid-template-columns: 150px 1fr;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e0e0e0;
          }
          .user-info-label {
            font-weight: bold;
            color: #555;
          }
          .user-info-value {
            color: #333;
          }
          @media print {
            body { padding: 20px; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Trade Pilot</h1>
          <h2>AI Copy Trading Program</h2>
        </div>

        <div class="user-info">
          <h4>User Information</h4>
          <div class="user-info-row">
            <span class="user-info-label">Full Name:</span>
            <span class="user-info-value">${template.name}</span>
          </div>
          <div class="user-info-row">
            <span class="user-info-label">Email:</span>
            <span class="user-info-value">${template.email}</span>
          </div>
          <div class="user-info-row">
            <span class="user-info-label">Role:</span>
            <span class="user-info-value">${template.role.charAt(0).toUpperCase() + template.role.slice(1)}</span>
          </div>
          <div class="user-info-row">
            <span class="user-info-label">Date Accepted:</span>
            <span class="user-info-value">${acceptanceDate}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">3️⃣ MANDATORY DISCLOSURE</div>
          
          <div class="disclosure-box">
            <h3>⚠️ TRADING RISK DISCLOSURE & LIABILITY RELEASE</h3>
            <p>Please read carefully. Trading involves risk.</p>
          </div>

          <p style="margin-bottom: 25px; text-align: justify;">
            By participating in the Trade Pilot AI Copy Trading Program, you acknowledge and agree to the following:
          </p>

          <div class="point">
            <h4>1. No Guaranteed Returns</h4>
            <p>The financial markets contain inherent risks. Past performance does not guarantee future results.</p>
          </div>

          <div class="point">
            <h4>2. Potential for Loss</h4>
            <p>You may experience losses—including loss of principal—while using this system.</p>
            <p>Earn Your Profits™️, Trade Pilot™️, its owners, traders, agents, and software developers are not liable for any financial loss incurred through market activity.</p>
          </div>

          <div class="point">
            <h4>3. Copy Trading Risk</h4>
            <p>While our master trader uses expert-level strategies, you understand that:</p>
            <ul>
              <li>Market conditions change</li>
              <li>Signals may vary</li>
              <li>Execution times may differ</li>
              <li>Results may vary user to user</li>
            </ul>
          </div>

          <div class="point">
            <h4>4. No Investment Advice</h4>
            <p>Earn Your Profits™️ does not provide personalized financial advice. This software provides automated signals and trade mirroring, not individualized guidance.</p>
          </div>

          <div class="point">
            <h4>5. You Maintain Full Control of Your Brokerage Account</h4>
            <p>You acknowledge:</p>
            <ul>
              <li>Your funds remain in your own account</li>
              <li>You can disconnect at any time</li>
              <li>You are solely responsible for deposits, withdrawals, and risk settings</li>
            </ul>
          </div>

          <div class="point">
            <h4>6. Hold Harmless Agreement</h4>
            <p>You agree to hold Earn Your Profits™️, its affiliates, owners, employees, contractors, developers, and traders harmless of all liability, including but not limited to:</p>
            <ul>
              <li>Market losses</li>
              <li>Software issues</li>
              <li>Technology delays</li>
              <li>Incorrect predictions</li>
              <li>Connectivity issues</li>
              <li>System disruptions</li>
            </ul>
          </div>

          <div class="point">
            <h4>7. Technology Disclaimer</h4>
            <p>You acknowledge that the AI system makes predictions based on algorithms, data models, and pattern recognition, but no prediction is guaranteed to be correct.</p>
          </div>

          <div class="point">
            <h4>8. Voluntary Participation</h4>
            <p>You are voluntarily choosing to use this system and understand all associated risks.</p>
          </div>

          <div class="point">
            <h4>9. No Refunds for Market Performance</h4>
            <p>The User understands that refunds will not be granted based solely on trading losses, market downturns, unprofitable trades, algorithm performance, account drawdowns, or dissatisfaction with market results. Refund requests, if any, may be reviewed on a case-by-case basis regarding billing or service-related issues only.</p>
          </div>
        </div>

        <div class="divider">⸻</div>

        <div class="section signature-section">
          <h3>4️⃣ SIGNATURE PAGE</h3>
          <h3 style="font-size: 18px; margin-top: 20px; margin-bottom: 20px;">AI TRADING SOFTWARE USER AGREEMENT & LIABILITY RELEASE</h3>

          <div class="checkbox-list">
            <div class="checkbox-item">I acknowledge that I have read and fully understand the Trading Risk Disclosure.</div>
            <div class="checkbox-item">I understand all risks associated with trading and automated/copy trading technology.</div>
            <div class="checkbox-item">I understand that Earn Your Profits™️ does not guarantee profits.</div>
            <div class="checkbox-item">I agree to hold Earn Your Profits™️ harmless from losses, damages, or legal claims.</div>
            <div class="checkbox-item">I voluntarily choose to participate and accept full responsibility for all trading outcomes.</div>
            <div class="checkbox-item">I confirm that I am in full control of my brokerage account.</div>
            <div class="checkbox-item">I understand that market results may vary and involve potential loss of principal.</div>
          </div>

          <div class="divider">⸻</div>

          <div class="signature-fields">
            <div class="signature-field">
              <label>Signature:</label>
              <div class="signature-img-container">
                <img src="${template.termsSignature}" alt="Digital Signature" class="signature-img" />
              </div>
            </div>
            <div class="signature-field">
              <label>Printed Name:</label>
              <div class="value">${template.termsPrintedName}</div>
            </div>
            <div class="signature-field">
              <label>Date:</label>
              <div class="value">${acceptanceDate}</div>
            </div>
            <div class="signature-field">
              <label>Email:</label>
              <div class="value">${template.termsEmail}</div>
            </div>
          </div>
        </div>

        <div style="margin-top: 50px; padding-top: 20px; border-top: 2px solid #ddd; text-align: center; color: #999; font-size: 12px;">
          <p>This document was generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p>Trade Pilot - AI Copy Trading Platform</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Terms-Agreement-${template.termsPrintedName.replace(/\s+/g, '-')}-${new Date(template.termsAcceptedAt).toISOString().split('T')[0]}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const templates: TermsTemplate[] = data?.users || [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = Math.max(1, pagination?.pages ?? 1);
  const currentPage = pagination?.page ?? page;

  if (isLoading && !data) return <Loader />;
  if (error) return <ErrorDisplay error="Failed to load terms templates" />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="lg:text-2xl text-md font-bold text-foreground">Terms & Conditions Templates</h2>
          <p className="text-muted lg:mt-2 mt-1 lg:text-lg text-[12px]">View all user terms acceptances and signatures</p>
        </div>
        <div className="text-sm text-muted">
          Total: <span className="font-semibold text-foreground">{total}</span>
          {isFetching && <span className="ml-2 text-xs">(updating…)</span>}
        </div>
      </div>

      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, printed name, or role..."
          className="w-full rounded-xl border border-border bg-surface pl-10 pr-10 py-2.5 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted hover:bg-background hover:text-foreground"
            aria-label="Clear search"
          >
            <FiX className="h-4 w-4" />
          </button>
        )}
      </div>

      {total === 0 && !debouncedSearch ? (
        <div className="card text-center py-12">
          <FiFileText className="h-16 w-16 text-muted mx-auto mb-4" />
          <h3 className="lg:text-lg text-md font-semibold text-foreground mb-2">No Terms Templates Yet</h3>
          <p className="text-muted lg:text-lg text-[14px]">Terms acceptances will appear here when users register</p>
        </div>
      ) : total === 0 ? (
        <div className="card text-center py-12">
          <FiSearch className="h-12 w-12 text-muted mx-auto mb-4" />
          <h3 className="lg:text-lg text-md font-semibold text-foreground mb-2">No matches found</h3>
          <p className="text-muted text-sm">Try a different name, email, or role.</p>
        </div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">User</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Printed Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Accepted Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template._id} className="border-b border-border hover:bg-surface-elevated transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-semibold text-foreground">{template.name}</p>
                        <p className="text-sm text-muted">{template.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-foreground">{template.termsPrintedName}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-muted text-sm">{template.termsEmail}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                        template.role === 'master' 
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {template.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-sm text-muted">
                        <FiCalendar className="h-4 w-4" />
                        {new Date(template.termsAcceptedAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleView(template)}
                          className="p-2 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                          title="View Signature"
                        >
                          <FiEye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(template)}
                          className="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                          title="Download"
                        >
                          <FiDownload className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(template)}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between rounded-2xl border border-border bg-surface px-4 py-3">
              <p className="text-xs text-muted">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg p-2 text-muted transition hover:bg-background hover:text-foreground disabled:opacity-40"
                >
                  <FiChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`min-w-[32px] rounded-lg px-2 py-1 text-xs font-semibold transition ${
                      n === currentPage
                        ? 'bg-primary text-white'
                        : 'text-muted hover:bg-background hover:text-foreground'
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-lg p-2 text-muted transition hover:bg-background hover:text-foreground disabled:opacity-40"
                >
                  <FiChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      {showModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Terms Acceptance Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <FiX className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <FiUser className="h-4 w-4" />
                    User Name
                  </label>
                  <p className="mt-1 text-gray-900 dark:text-white font-medium">{selectedTemplate.name}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <FiMail className="h-4 w-4" />
                    Email
                  </label>
                  <p className="mt-1 text-gray-900 dark:text-white font-medium">{selectedTemplate.email}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                    Printed Name
                  </label>
                  <p className="mt-1 text-gray-900 dark:text-white font-medium">{selectedTemplate.termsPrintedName}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <FiCalendar className="h-4 w-4" />
                    Accepted Date
                  </label>
                  <p className="mt-1 text-gray-900 dark:text-white font-medium">
                    {new Date(selectedTemplate.termsAcceptedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
                  Digital Signature
                </label>
                <div className="border-2 border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white">
                  <img 
                    src={selectedTemplate.termsSignature} 
                    alt="Signature" 
                    className="max-w-full h-auto"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => handleDownload(selectedTemplate)}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <FiDownload className="h-4 w-4" />
                  Download
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <FiTrash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Delete Terms Template</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <span className="font-semibold">User:</span> {templateToDelete.name}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <span className="font-semibold">Email:</span> {templateToDelete.email}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold">Printed Name:</span> {templateToDelete.termsPrintedName}
                </p>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Are you sure you want to delete this terms template? This will also delete the associated user account and all related data.
              </p>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTemplateToDelete(null);
                  }}
                  className="px-6 py-2 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 font-semibold transition-colors"
                  disabled={deleteUserMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleteUserMutation.isPending}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteUserMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 className="h-4 w-4" />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsTemplates;

